import { useEffect, useState } from 'react';
import { Settings2, Save, CheckCircle2, AlertCircle, Plus, Trash2, ArrowUp, ArrowDown, Calculator, Sparkles, FolderPlus } from 'lucide-react';
import { getRule, saveRule } from '@/features/vendor/api/vendorPricingApi';

const PRICE_KIND = 'representative';

const TARGET_OPTIONS = [
    { value: 'production_price', label: 'قیمت تولید' },
    { value: 'rep_price', label: 'قیمت نمایندگی' },
    { value: 'branch_price', label: 'قیمت شعبه' },
    { value: 'cost_price', label: 'قیمت تمام شده' },
];

const CONCEPT_OPTIONS = [
    { value: 'consumer_price', label: 'قیمت مصرف‌کننده' },
    { value: 'production_price', label: 'قیمت تولید' },
    { value: 'rep_price', label: 'قیمت نمایندگی' },
    { value: 'branch_price', label: 'قیمت شعبه' },
];

const OPERATOR_OPTIONS = [
    { value: 'add', label: '+' },
    { value: 'subtract', label: '−' },
    { value: 'multiply', label: '×' },
    { value: 'divide', label: '÷' },
];

const DEFAULT_PROFILES = [
    {
        id: 'default',
        name: 'الگوی پیش‌فرض',
        formulas: [
            {
                id: 1,
                target: 'production_price',
                expression: [
                    { type: 'concept', value: 'consumer_price' },
                    { type: 'operator', value: 'divide' },
                    { type: 'number', value: 1.1 },
                    { type: 'operator', value: 'divide' },
                    { type: 'number', value: 1.11 }
                ]
            },
            {
                id: 2,
                target: 'rep_price',
                expression: [
                    { type: 'concept', value: 'production_price' },
                    { type: 'operator', value: 'divide' },
                    { type: 'number', value: 1.1 }
                ]
            }
        ]
    }
];

export default function VendorPricingRulesPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [profiles, setProfiles] = useState(DEFAULT_PROFILES);
    const [activeProfileId, setActiveProfileId] = useState('default');

    const [sampleConsumerPrice, setSampleConsumerPrice] = useState(10000000);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const currentRule = await getRule(PRICE_KIND);
                // 👈 خواندن مستقیم و اصولی از دیتابیس
                if (currentRule && currentRule.rule_type === 'custom_profiles' && currentRule.params?.profiles) {
                    setProfiles(currentRule.params.profiles);
                    setActiveProfileId(currentRule.params.profiles[0]?.id || '');
                }
            } catch (err) {
                console.log('تنظیمی یافت نشد، فرمول‌های پیش‌فرض اعمال شد.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const activeProfile = profiles.find(p => p.id === activeProfileId) || profiles[0];

    const handleAddProfile = () => {
        const newId = `profile_${Date.now()}`;
        const newProfile = {
            id: newId,
            name: 'الگوی جدید',
            formulas: [{ id: 1, target: 'rep_price', expression: [{ type: 'concept', value: 'consumer_price' }] }]
        };
        setProfiles([...profiles, newProfile]);
        setActiveProfileId(newId);
    };

    const handleRemoveProfile = (id) => {
        if (profiles.length === 1) return alert('حداقل یک الگو باید وجود داشته باشد.');
        if (window.confirm('آیا از حذف این الگو مطمئن هستید؟')) {
            const newProfiles = profiles.filter(p => p.id !== id);
            setProfiles(newProfiles);
            if (activeProfileId === id) setActiveProfileId(newProfiles[0].id);
        }
    };

    const handleProfileNameChange = (id, newName) => {
        setProfiles(profiles.map(p => p.id === id ? { ...p, name: newName } : p));
    };

    const updateActiveProfileFormulas = (newFormulas) => {
        setProfiles(profiles.map(p => p.id === activeProfileId ? { ...p, formulas: newFormulas } : p));
    };

    const handleAddFormula = () => {
        const formulas = activeProfile.formulas;
        const newId = formulas.length > 0 ? Math.max(...formulas.map(f => f.id)) + 1 : 1;
        updateActiveProfileFormulas([
            ...formulas,
            { id: newId, target: 'rep_price', expression: [{ type: 'concept', value: 'consumer_price' }] }
        ]);
    };

    const handleRemoveFormula = (id) => {
        updateActiveProfileFormulas(activeProfile.formulas.filter(f => f.id !== id));
    };

    const handleChangeTarget = (id, value) => {
        updateActiveProfileFormulas(activeProfile.formulas.map(f => f.id === id ? { ...f, target: value } : f));
    };

    const addNode = (formulaId, type) => {
        if (!type) return;
        updateActiveProfileFormulas(activeProfile.formulas.map(f => {
            if (f.id === formulaId) {
                let initialValue = type === 'number' ? 1 : (type === 'operator' ? 'divide' : 'consumer_price');
                return { ...f, expression: [...f.expression, { type, value: initialValue }] };
            }
            return f;
        }));
    };

    const updateNode = (formulaId, index, value) => {
        updateActiveProfileFormulas(activeProfile.formulas.map(f => {
            if (f.id === formulaId) {
                const newExpr = [...f.expression];
                newExpr[index] = { ...newExpr[index], value };
                return { ...f, expression: newExpr };
            }
            return f;
        }));
    };

    const removeNode = (formulaId, index) => {
        updateActiveProfileFormulas(activeProfile.formulas.map(f => {
            if (f.id === formulaId) {
                const newExpr = [...f.expression];
                newExpr.splice(index, 1);
                return { ...f, expression: newExpr };
            }
            return f;
        }));
    };

    const moveFormula = (index, direction) => {
        const newFormulas = [...activeProfile.formulas];
        if (direction === 'up' && index > 0) {
            [newFormulas[index - 1], newFormulas[index]] = [newFormulas[index], newFormulas[index - 1]];
        } else if (direction === 'down' && index < activeProfile.formulas.length - 1) {
            [newFormulas[index + 1], newFormulas[index]] = [newFormulas[index], newFormulas[index + 1]];
        }
        updateActiveProfileFormulas(newFormulas);
    };

    const handleSuggest = () => {
        if (window.confirm('فرمول‌های استاندارد جایگزین فرمول‌های این الگو شوند؟')) {
            updateActiveProfileFormulas(DEFAULT_PROFILES[0].formulas);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage({ type: '', text: '' });
        try {
            await saveRule(PRICE_KIND, {
                rule_type: 'custom_profiles', // 👈 حالا کاملاً اصولی به بک‌اند ارسال می‌شود
                params: { profiles: profiles }
            });
            setMessage({ type: 'success', text: 'الگوهای محاسباتی با موفقیت ذخیره شدند.' });
        } catch (err) {
            setMessage({ type: 'error', text: 'خطا در ذخیره: ' + err.message });
        } finally {
            setSaving(false);
        }
    };

    const calculatePreview = (formulas) => {
        let prices = { consumer_price: Number(sampleConsumerPrice) || 0 };
        formulas.forEach(f => {
            if (!f.expression || f.expression.length === 0) return;
            let result = 0;
            let currentOp = null;

            f.expression.forEach(node => {
                if (node.type === 'concept') {
                    const val = prices[node.value] || 0;
                    if (currentOp === null) result = val;
                    else {
                        if (currentOp === 'add') result += val;
                        if (currentOp === 'subtract') result -= val;
                        if (currentOp === 'multiply') result *= val;
                        if (currentOp === 'divide') result = val !== 0 ? result / val : 0;
                    }
                } else if (node.type === 'number') {
                    const val = Number(node.value) || 0;
                    if (currentOp === null) result = val;
                    else {
                        if (currentOp === 'add') result += val;
                        if (currentOp === 'subtract') result -= val;
                        if (currentOp === 'multiply') result *= val;
                        if (currentOp === 'divide') result = val !== 0 ? result / val : 0;
                    }
                } else if (node.type === 'operator') {
                    currentOp = node.value;
                }
            });
            prices[f.target] = Math.round(result);
        });
        return prices;
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}>در حال دریافت اطلاعات...</div>;

    const previewData = calculatePreview(activeProfile?.formulas || []);

    return (
        <div style={{ direction: 'rtl', width: '100%', margin: '-1.5rem 0 0 0', padding: '0 0 1rem 0' }}>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', width: '100%' }}>

                <div style={{ width: '200px', background: '#fff', padding: '0.75rem', borderRadius: '12px', border: '1px solid #e5e7eb', flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <h3 style={{ margin: 0, fontSize: '0.85rem', color: '#1e293b' }}>الگوهای محاسباتی</h3>
                        <button onClick={handleAddProfile} style={{ background: '#e0e7ff', color: '#4338ca', border: 'none', padding: '0.2rem', borderRadius: '4px', cursor: 'pointer' }} title="الگوی جدید"><FolderPlus size={14} /></button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        {profiles.map(p => (
                            <div key={p.id} onClick={() => setActiveProfileId(p.id)} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem', borderRadius: '6px', cursor: 'pointer', background: activeProfileId === p.id ? '#eff6ff' : '#f8fafc', border: activeProfileId === p.id ? '1px solid #bfdbfe' : '1px solid #e2e8f0' }}>
                                <input
                                    value={p.name}
                                    onChange={(e) => handleProfileNameChange(p.id, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontWeight: activeProfileId === p.id ? 'bold' : 'normal', color: activeProfileId === p.id ? '#1e40af' : '#64748b', fontSize: '0.75rem', outline: 'none' }}
                                />
                                {profiles.length > 1 && (
                                    <button onClick={(e) => { e.stopPropagation(); handleRemoveProfile(p.id); }} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex' }}><Trash2 size={12} /></button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ flex: 1, minWidth: 0, background: '#fff', padding: '1rem', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Settings2 size={20} color="#2563eb" />
                            <h1 style={{ margin: 0, fontSize: '1.05rem', color: '#1e293b' }}>فرمول‌های الگوی: {activeProfile?.name}</h1>
                        </div>
                        <button onClick={handleSuggest} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#f5f3ff', color: '#7e22ce', border: '1px solid #ddd6fe', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}>
                            <Sparkles size={14} /> پیشنهاد فرمول
                        </button>
                    </div>

                    {message.text && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: message.type === 'success' ? '#dcfce7' : '#fee2e2', color: message.type === 'success' ? '#15803d' : '#b91c1c', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem', fontWeight: 'bold' }}>
                            {message.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                            {message.text}
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                        {activeProfile?.formulas.map((formula, index) => (
                            <div key={formula.id} style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '0.5rem' }}>

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.4rem', marginBottom: '0.4rem' }}>
                                    <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '0.1rem 0.5rem', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.75rem' }}>فرمول {index + 1}</span>
                                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                                        <button onClick={() => moveFormula(index, 'up')} disabled={index === 0} style={{ background: '#fff', border: '1px solid #cbd5e1', padding: '0.2rem', borderRadius: '4px', cursor: index === 0 ? 'not-allowed' : 'pointer', opacity: index === 0 ? 0.5 : 1 }}><ArrowUp size={14} /></button>
                                        <button onClick={() => moveFormula(index, 'down')} disabled={index === activeProfile.formulas.length - 1} style={{ background: '#fff', border: '1px solid #cbd5e1', padding: '0.2rem', borderRadius: '4px', cursor: index === activeProfile.formulas.length - 1 ? 'not-allowed' : 'pointer', opacity: index === activeProfile.formulas.length - 1 ? 0.5 : 1 }}><ArrowDown size={14} /></button>
                                        <button onClick={() => handleRemoveFormula(formula.id)} style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', padding: '0.2rem 0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.75rem' }}><Trash2 size={12} /></button>
                                    </div>
                                </div>

                                <div style={{
                                    direction: 'ltr',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    background: '#fff',
                                    padding: '0.5rem',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    minHeight: '40px'
                                }}>
                                    <select value={formula.target} onChange={(e) => handleChangeTarget(formula.id, e.target.value)} style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #c4b5fd', fontWeight: 'bold', color: '#6d28d9', background: '#ede9fe', fontSize: '0.75rem', outline: 'none', cursor: 'pointer' }}>
                                        {TARGET_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                    <span style={{ fontWeight: 'bold', color: '#6d28d9', fontSize: '1rem', margin: '0 0.25rem' }}>=</span>

                                    {formula.expression.map((node, nodeIndex) => (
                                        <div key={nodeIndex} style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', borderRadius: '4px', border: '1px solid #cbd5e1', overflow: 'hidden' }}>
                                            {node.type === 'concept' && (
                                                <select value={node.value} onChange={(e) => updateNode(formula.id, nodeIndex, e.target.value)} style={{ border: 'none', background: 'transparent', padding: '0.25rem 0.3rem', fontSize: '0.75rem', outline: 'none', fontWeight: 'bold', color: '#2563eb', cursor: 'pointer' }}>
                                                    {CONCEPT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                </select>
                                            )}
                                            {node.type === 'number' && (
                                                <input type="number" step="0.01" value={node.value} onChange={(e) => updateNode(formula.id, nodeIndex, e.target.value)} style={{ border: 'none', background: 'transparent', padding: '0.25rem 0.3rem', width: '55px', fontSize: '0.75rem', outline: 'none', fontWeight: 'bold', color: '#1e293b' }} />
                                            )}
                                            {node.type === 'operator' && (
                                                <select value={node.value} onChange={(e) => updateNode(formula.id, nodeIndex, e.target.value)} style={{ border: 'none', background: 'transparent', padding: '0.25rem 0.3rem', fontSize: '0.85rem', outline: 'none', fontWeight: 'bold', color: '#ef4444', cursor: 'pointer' }}>
                                                    {OPERATOR_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                </select>
                                            )}
                                            <button onClick={() => removeNode(formula.id, nodeIndex)} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0 0.2rem', display: 'flex', alignItems: 'center' }}>
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    ))}

                                    <select onChange={(e) => { addNode(formula.id, e.target.value); e.target.value = ""; }} style={{ width: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#fff', background: '#2563eb', border: 'none', borderRadius: '4px', cursor: 'pointer', outline: 'none', appearance: 'none', WebkitAppearance: 'none', textAlign: 'center', marginLeft: '0.25rem' }}>
                                        <option value="" disabled style={{ color: '#000', background: '#fff' }}>+ افزودن</option>
                                        <option value="concept" style={{ color: '#000', background: '#fff' }}>مفهوم</option>
                                        <option value="number" style={{ color: '#000', background: '#fff' }}>عدد</option>
                                        <option value="operator" style={{ color: '#000', background: '#fff' }}>عملگر</option>
                                    </select>
                                </div>
                            </div>
                        ))}
                    </div>

                    <button onClick={handleAddFormula} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#e0e7ff', color: '#4338ca', border: '1px dashed #a5b4fc', padding: '0.6rem 1rem', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', width: '100%', justifyContent: 'center', fontSize: '0.85rem', marginBottom: '1rem' }}>
                        <Plus size={16} /> افزودن فرمول جدید به این الگو
                    </button>

                    <div style={{ background: '#eff6ff', padding: '1rem', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: '#1e40af', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <Calculator size={16} /> پیش‌نمایش محاسبات این الگو
                        </h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                            <label style={{ fontWeight: 'bold', color: '#1e40af', fontSize: '0.8rem' }}>قیمت مصرف‌کننده (ریال):</label>
                            <input type="number" value={sampleConsumerPrice} onChange={(e) => setSampleConsumerPrice(Number(e.target.value) || 0)} style={{ flex: '1 1 150px', padding: '0.4rem', borderRadius: '4px', border: '1px solid #93c5fd', direction: 'ltr', fontWeight: 'bold', fontSize: '0.85rem', outline: 'none' }} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem' }}>
                            <div style={{ background: '#fff', padding: '0.75rem', borderRadius: '4px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                                <span style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', marginBottom: '0.25rem' }}>قیمت تولید</span>
                                <strong style={{ fontSize: '0.9rem', color: '#2563eb' }}>{previewData.production_price?.toLocaleString('fa-IR') || '-'}</strong>
                            </div>
                            <div style={{ background: '#fff', padding: '0.75rem', borderRadius: '4px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                                <span style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', marginBottom: '0.25rem' }}>قیمت نمایندگی</span>
                                <strong style={{ fontSize: '0.9rem', color: '#10b981' }}>{previewData.rep_price?.toLocaleString('fa-IR') || '-'}</strong>
                            </div>
                            <div style={{ background: '#fff', padding: '0.75rem', borderRadius: '4px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                                <span style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', marginBottom: '0.25rem' }}>قیمت شعبه</span>
                                <strong style={{ fontSize: '0.9rem', color: '#8b5cf6' }}>{previewData.branch_price?.toLocaleString('fa-IR') || '-'}</strong>
                            </div>
                            <div style={{ background: '#fff', padding: '0.75rem', borderRadius: '4px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                                <span style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', marginBottom: '0.25rem' }}>قیمت تمام شده</span>
                                <strong style={{ fontSize: '0.9rem', color: '#ef4444' }}>{previewData.cost_price?.toLocaleString('fa-IR') || '-'}</strong>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                        <button onClick={() => setProfiles(JSON.parse(JSON.stringify(DEFAULT_PROFILES)))} style={{ background: '#fff', color: '#475569', border: '1px solid #cbd5e1', padding: '0.6rem 1.5rem', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 'bold', cursor: 'pointer' }}>
                            انصراف
                        </button>
                        <button onClick={handleSave} disabled={saving} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '0.6rem 2rem', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 'bold', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)' }}>
                            <Save size={16} />
                            {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
}